import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { PerformanceMarker } from '@estee/elc-telemetry';
import {
    AllServicesTypes,
    AllUpdatedServicesTypes,
    IAppConfig,
    IManifestServiceRegistryComp,
    root
} from '@estee/elc-universal-utils';
import { getServices, hasServiceStarted, isServiceLazy } from '../utils';

enum ServiceQueryAndEmitFields {
    events = 'events',
    queries = 'queries'
}

export class ServiceLazyLoader {
    constructor(
        private logger: ELCLogger,
        private marker: PerformanceMarker,
        private appConfig: IAppConfig = {}
    ) {}

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private beforeEmit = async (action: string, payload: any, next: Function) => {
        const mark = `lazy_emit_${action}`;
        this.marker.startMark(mark);
        await this.maybeStartService(ServiceQueryAndEmitFields.events)(action);
        this.marker.endMark(mark);

        next(action, payload);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private beforeQuery = async (queryName: string, payload: any, next: Function) => {
        const mark = `lazy_query_${queryName}`;
        this.marker.startMark(mark);
        await this.maybeStartService(ServiceQueryAndEmitFields.queries)(queryName);
        this.marker.endMark(mark);

        return next(queryName, payload);
    };

    private maybeStartService = (key: keyof IManifestServiceRegistryComp) => async (
        item: string
    ) => {
        const srv = Object.values(getServices()).find((service: AllServicesTypes) => {
            if (isServiceLazy(service as AllUpdatedServicesTypes) && !hasServiceStarted(service)) {
                const _service = service as IManifestServiceRegistryComp;

                return (_service[key] as string[]).indexOf(item) !== -1;
            }

            return false;
        }) as IManifestServiceRegistryComp | undefined;

        if (srv && srv.name) {
            const message = `Starting ${srv.name} service due to ${item} ${
                key === 'queries' ? 'query' : 'event'
            }!`;
            this.logger.info({
                message,
                triggerType: TriggerType.render
            });
            const mark = `lazy_initialize_${srv.name}`;

            this.marker.startMark(mark);
            await srv.start(this.appConfig);
            this.marker.endMark(mark);
        }
    };

    public load() {
        root.GlobalServiceBus.applyHook(() => ({
            beforeEmit: this.beforeEmit,
            beforeQuery: this.beforeQuery
        }));
    }
}
